import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getWatcherSettings } from '../../redux/actions/configActions';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

import Content from '../../components/Content';
import Loader from '../../components/Loader';
import NetworkError from '../../components/Error';
import Button from '../../components/Button';

import WatcherSettingsSummaryStyles from './WatcherSettingsSummary.module.css';

const FRONTEND_WATCHER_SETTINGS_SUMMARY_ERROR_CODES = {
	INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
};

const buildWatcherSettingsSummarySupportError = (errorCode, pageInfo, sourceMessage) =>
	formatFrontendSupportError({
		errorCode,
		pageInfo,
		sourceMessage,
	});

const isEnabled = (value) => {
	if (typeof value === 'boolean') return value;
	if (typeof value === 'number') return value === 1;
	if (typeof value === 'string') {
		const normalized = value.trim().toLowerCase();
		return normalized === '1' || normalized === 'true' || normalized === 'enabled';
	}
	return false;
};

const displayValue = (value) => {
	if (value === null || value === undefined) return 'N/A';
	if (typeof value === 'string') return value.trim() !== '' ? value : 'N/A';
	return String(value);
};

const WatcherSettingsSummaryScreen = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [ dataLoading, setDataLoading ] = React.useState(false);
	const [ settingsData, setSettingsData ] = React.useState({});
	const [ supportErrorMessage, setSupportErrorMessage ] = React.useState('');
	const [ copiedSupportError, setCopiedSupportError ] = React.useState(false);

	const setWatcherSettingsSummarySupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
		setSupportErrorMessage(buildWatcherSettingsSummarySupportError(errorCode, pageInfo, sourceMessage));
		setCopiedSupportError(false);
	}, []);

	const clearWatcherSettingsSummarySupportError = React.useCallback(() => {
		setSupportErrorMessage('');
		setCopiedSupportError(false);
	}, []);

	const handleCopySupportError = React.useCallback(async () => {
		if (!supportErrorMessage) return;
		try {
			await navigator.clipboard.writeText(supportErrorMessage);
			setCopiedSupportError(true);
			setTimeout(() => setCopiedSupportError(false), 2000);
		} catch (error) {
			setCopiedSupportError(false);
		}
	}, [ supportErrorMessage ]);

	React.useEffect(() => {
		const getData = async () => {
			setDataLoading(true);
			clearWatcherSettingsSummarySupportError();
			try {
				const data = await dispatch(getWatcherSettings());
				if (data && data.status) {
					setSettingsData(data);
					clearWatcherSettingsSummarySupportError();
				} else {
					setSettingsData({});
					setWatcherSettingsSummarySupportError(
						FRONTEND_WATCHER_SETTINGS_SUMMARY_ERROR_CODES.INITIAL_LOAD_FAILED,
						'Watcher Settings Summary Page / Initial Data Load Failed',
						data?.msg || (typeof data === 'string' ? data : 'Watcher settings request did not return success')
					);
				}
			} catch (error) {
				setSettingsData({});
				setWatcherSettingsSummarySupportError(
					FRONTEND_WATCHER_SETTINGS_SUMMARY_ERROR_CODES.INITIAL_LOAD_FAILED,
					'Watcher Settings Summary Page / Initial Data Request Error',
					error?.message || String(error)
				);
			} finally {
				setDataLoading(false);
			}
		};

		getData();
	}, [ dispatch, clearWatcherSettingsSummarySupportError, setWatcherSettingsSummarySupportError ]);

	return (
		<Content currentMenu='configuration' headerTitle='Settings'>
			{dataLoading ? (
				<Loader />
			) : settingsData && Object.keys(settingsData).length === 0 ? (
				<div>
					<NetworkError />
					{supportErrorMessage && (
						<div className={WatcherSettingsSummaryStyles.supportErrorCard}>
							<strong>Support Error:</strong>
							<code className={WatcherSettingsSummaryStyles.supportErrorCode}>{supportErrorMessage}</code>
							<button
								type='button'
								className={WatcherSettingsSummaryStyles.copySupportErrorButton}
								onClick={handleCopySupportError}
							>
								{copiedSupportError ? 'Copied' : 'Copy Error Details'}
							</button>
						</div>
					)}
				</div>
			) : (
				<div className={WatcherSettingsSummaryStyles.content}>
					{supportErrorMessage && (
						<div className={WatcherSettingsSummaryStyles.supportErrorBanner}>
							<div className={WatcherSettingsSummaryStyles.supportErrorBannerText}>
								<strong>Support Error:</strong>
								<code className={WatcherSettingsSummaryStyles.supportErrorCode}>{supportErrorMessage}</code>
							</div>
							<button
								type='button'
								className={WatcherSettingsSummaryStyles.copySupportErrorButton}
								onClick={handleCopySupportError}
							>
								{copiedSupportError ? 'Copied' : 'Copy Error Details'}
							</button>
						</div>
					)}

					<div className={WatcherSettingsSummaryStyles.card} data-testid='settings-summary-card'>
						<div className={WatcherSettingsSummaryStyles.cardHeader}>
							<div className={WatcherSettingsSummaryStyles.cardIcon}>
								<svg
									xmlns='http://www.w3.org/2000/svg'
									viewBox='0 0 24 24'
									fill='none'
									stroke='currentColor'
									strokeWidth='2'
									strokeLinecap='round'
									strokeLinejoin='round'
								>
									<path d='M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z' />
									<circle cx='12' cy='12' r='3' />
								</svg>
							</div>
							<h2>Settings</h2>
						</div>

						<div className={WatcherSettingsSummaryStyles.cardBody}>
							<div className={WatcherSettingsSummaryStyles.cardInfo}>
								<span className={WatcherSettingsSummaryStyles.label}>Email Notifications:</span>
								<span className={WatcherSettingsSummaryStyles.value}>{isEnabled(settingsData.EmailEnabled) ? 'Enabled' : 'Disabled'}</span>
							</div>
							<div className={WatcherSettingsSummaryStyles.cardInfo}>
								<span className={WatcherSettingsSummaryStyles.label}>Mail Server:</span>
								<span className={WatcherSettingsSummaryStyles.value}>{displayValue(settingsData.MailProvider === 'smtp' ? settingsData.SmtpHost : 'Microsoft 365 (Graph)')}</span>
							</div>
							<div className={WatcherSettingsSummaryStyles.cardInfo}>
								<span className={WatcherSettingsSummaryStyles.label}>SMTP Port:</span>
								<span className={WatcherSettingsSummaryStyles.value}>{displayValue(settingsData.MailProvider === 'smtp' ? settingsData.SmtpPort : 'N/A')}</span>
							</div>
							<div className={WatcherSettingsSummaryStyles.cardInfo}>
								<span className={WatcherSettingsSummaryStyles.label}>From Address:</span>
								<span className={WatcherSettingsSummaryStyles.value}>{displayValue(settingsData.MailProvider === 'smtp' ? settingsData.SmtpSender : settingsData.GraphSender)}</span>
							</div>
							<div className={WatcherSettingsSummaryStyles.cardInfo}>
								<span className={WatcherSettingsSummaryStyles.label}>Recipient(s):</span>
								<span className={WatcherSettingsSummaryStyles.value}>{displayValue(settingsData.GraphRecipient)}</span>
							</div>
							<div className={WatcherSettingsSummaryStyles.cardInfo}>
								<span className={WatcherSettingsSummaryStyles.label}>Air-Gap Mode:</span>
								<span className={WatcherSettingsSummaryStyles.value}>{isEnabled(settingsData.AirgapMode) ? 'On' : 'Off'}</span>
							</div>
							<div className={WatcherSettingsSummaryStyles.cardInfo}>
								<span className={WatcherSettingsSummaryStyles.label}>Require MFA:</span>
								<span className={WatcherSettingsSummaryStyles.value}>{settingsData.RequireMfa !== false ? 'Required' : 'Disabled'}</span>
							</div>
							<div className={WatcherSettingsSummaryStyles.cardInfo}>
								<span className={WatcherSettingsSummaryStyles.label}>Syslog Enabled:</span>
								<span className={WatcherSettingsSummaryStyles.value}>{isEnabled(settingsData.SyslogEnable) ? 'Enabled' : 'Disabled'}</span>
							</div>
							<div className={WatcherSettingsSummaryStyles.cardInfo}>
								<span className={WatcherSettingsSummaryStyles.label}>Syslog Server:</span>
								<span className={WatcherSettingsSummaryStyles.value}>{displayValue(settingsData.SyslogServer)}</span>
							</div>
						</div>

						<div className={WatcherSettingsSummaryStyles.cardActions}>
							<Button label='Edit Settings' onClick={() => navigate('/config-settings')} />
						</div>
					</div>
				</div>
			)}
		</Content>
	);
};

export default WatcherSettingsSummaryScreen;
